import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { useI18n } from '../i18n';
import { useAds } from '../monetization/AdsProvider';
import { RootState } from '../store';

import { BirthInput, BirthPlace, computeChart } from './astro/chart';
import { ChartView } from './astro/ChartView';
import {
    City,
    knownZones,
    loadCities,
    parseManualPlace,
    searchCities,
    searchOnline,
} from './astro/places';
import { PageShell } from './components/PageShell';
import { newReadingId } from './draw';
import { buzz, playCue } from './feedback';
import { deleteChart, saveChart } from './tarotSlice';

type FormError = 'date' | 'time' | 'place' | 'lat' | 'lon' | 'zone' | 'calc';

const todayIso = (): string => {
    const d = new Date();
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const MIN_DATE = '1800-01-01';

type Index = Awaited<ReturnType<typeof loadCities>>;

const useCityLabel = (language: string) =>
    useMemo(() => {
        let names: Intl.DisplayNames | null = null;
        try {
            names = new Intl.DisplayNames([language], { type: 'region' });
        } catch {
            names = null;
        }
        return (c: City) => {
            let country = c.country;
            try {
                country = names?.of(c.country) ?? c.country;
            } catch {
                // An unknown region code: show the code.
            }
            const name = language === 'pt-BR' && c.namePt ? c.namePt : c.name;
            return country ? `${name}, ${country}` : name;
        };
    }, [language]);

/** The form that asks when and where, then the video gate, then the chart. */
const ChartForm: React.FC = () => {
    const { t, language } = useI18n();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { requestUnlock } = useAds();
    const saved = useSelector((s: RootState) => s.tarot.charts);
    const cityLabel = useCityLabel(language);

    const [name, setName] = useState('');
    const [date, setDate] = useState('');
    const [time, setTime] = useState('');
    const [timeUnknown, setTimeUnknown] = useState(false);
    const [index, setIndex] = useState<Index | null>(null);
    const [query, setQuery] = useState('');
    const [city, setCity] = useState<City | null>(null);
    const [manual, setManual] = useState(false);
    const [manualPlace, setManualPlace] = useState({
        name: '',
        lat: '',
        lon: '',
        zone: '',
    });
    const [error, setError] = useState<FormError | null>(null);
    const [busy, setBusy] = useState(false);
    const [dismissed, setDismissed] = useState(false);
    const [deleting, setDeleting] = useState<string | null>(null);

    useEffect(() => {
        let alive = true;
        loadCities()
            .then((loaded) => alive && setIndex(loaded))
            .catch(() => alive && setManual(true));
        return () => {
            alive = false;
        };
    }, []);

    const local = useMemo(
        () => (index ? searchCities(index, query) : []),
        [index, query]
    );
    const [online, setOnline] = useState<{ query: string; found: City[] }>({
        query: '',
        found: [],
    });
    const [searching, setSearching] = useState(false);

    // Places the bundled list lacks (small towns): look them up online once typing pauses.
    const searchable =
        !!index &&
        local.length === 0 &&
        query.trim().length >= 3 &&
        !(city && query === cityLabel(city));
    useEffect(() => {
        if (!searchable) {
            setSearching(false);
            return;
        }
        const controller = new AbortController();
        setSearching(true);
        const timer = setTimeout(() => {
            searchOnline(query, controller.signal).then((found) => {
                if (controller.signal.aborted) return;
                setOnline({ query, found });
                setSearching(false);
            });
        }, 500);
        return () => {
            clearTimeout(timer);
            controller.abort();
        };
    }, [searchable, query]);
    const options =
        local.length > 0 ? local : online.query === query ? online.found : [];
    const zones = useMemo(() => knownZones(), []);

    const submit = async () => {
        if (busy) return;
        setDismissed(false);

        const [y, m, d] = date.split('-').map(Number);
        if (
            !y ||
            !m ||
            !d ||
            date < MIN_DATE ||
            date > todayIso() ||
            Number.isNaN(new Date(y, m - 1, d).getTime())
        )
            return setError('date');

        let birthTime: BirthInput['time'] = null;
        if (!timeUnknown) {
            const match = /^(\d{1,2}):(\d{2})$/.exec(time);
            if (!match) return setError('time');
            birthTime = { hour: Number(match[1]), minute: Number(match[2]) };
        }

        let place: BirthPlace;
        if (manual) {
            const parsed = parseManualPlace(manualPlace);
            if ('error' in parsed) return setError(parsed.error);
            place = parsed.place;
        } else {
            if (!city) return setError('place');
            place = {
                name: cityLabel(city),
                lat: city.lat,
                lon: city.lon,
                zone: city.zone,
            };
        }

        const input: BirthInput = {
            year: y,
            month: m,
            day: d,
            time: birthTime,
            place,
        };
        // Calculate before the video: a chart that cannot be made should not cost a video.
        try {
            computeChart(input);
        } catch {
            return setError('calc');
        }
        setError(null);

        setBusy(true);
        const ok = await requestUnlock();
        setBusy(false);
        if (!ok) return setDismissed(true);

        const id = newReadingId();
        dispatch(saveChart({ id, at: Date.now(), name: name.trim(), input }));
        buzz();
        playCue('reveal');
        // Replace the form in the history, so going back from the chart does not land on it again.
        navigate(`/birth-chart/${id}`, { replace: true });
    };

    return (
        <PageShell to="/">
            <Typography variant="h4" component="h1" gutterBottom>
                {t('chart.title')}
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
                {t('chart.intro')}
            </Typography>

            <Stack
                component="form"
                noValidate
                spacing={2}
                onSubmit={(e) => {
                    e.preventDefault();
                    void submit();
                }}
            >
                <TextField
                    label={t('chart.name')}
                    value={name}
                    onChange={(e) => setName(e.target.value.slice(0, 30))}
                    inputProps={{ maxLength: 30 }}
                    autoComplete="off"
                />
                <TextField
                    label={t('chart.date')}
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: MIN_DATE, max: todayIso() }}
                    error={error === 'date'}
                    helperText={error === 'date' ? t('chart.error.date') : ''}
                    required
                />
                <Box>
                    <TextField
                        label={t('chart.time')}
                        type="time"
                        value={timeUnknown ? '' : time}
                        onChange={(e) => setTime(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        disabled={timeUnknown}
                        error={error === 'time'}
                        helperText={
                            error === 'time' ? t('chart.error.time') : ''
                        }
                        fullWidth
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={timeUnknown}
                                onChange={(e) =>
                                    setTimeUnknown(e.target.checked)
                                }
                            />
                        }
                        label={t('chart.timeUnknown')}
                    />
                    {timeUnknown && (
                        <Typography variant="caption" color="text.secondary">
                            {t('chart.timeUnknownHint')}
                        </Typography>
                    )}
                </Box>

                {!manual ? (
                    <Box>
                        <Autocomplete<City>
                            options={options}
                            value={city}
                            onChange={(_, value) => setCity(value)}
                            inputValue={query}
                            onInputChange={(_, value) => setQuery(value)}
                            filterOptions={(all) => all}
                            getOptionLabel={cityLabel}
                            isOptionEqualToValue={(a, b) =>
                                a.lat === b.lat &&
                                a.lon === b.lon &&
                                a.name === b.name
                            }
                            loading={!index || searching}
                            loadingText={
                                index
                                    ? t('chart.placeSearching')
                                    : t('chart.placeLoading')
                            }
                            noOptionsText={
                                query.trim().length < 2
                                    ? t('chart.placeHint')
                                    : t('chart.placeNone')
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={t('chart.place')}
                                    placeholder={t('chart.placeHint')}
                                    error={error === 'place'}
                                    helperText={
                                        error === 'place'
                                            ? t('chart.error.place')
                                            : city
                                              ? t('chart.placeChosen', {
                                                    zone: city.zone,
                                                })
                                              : ''
                                    }
                                />
                            )}
                        />
                        <Button
                            size="small"
                            onClick={() => setManual(true)}
                            sx={{ mt: 0.5 }}
                        >
                            {t('chart.manual')}
                        </Button>
                    </Box>
                ) : (
                    <Stack spacing={2}>
                        <TextField
                            label={t('chart.manualPlace')}
                            value={manualPlace.name}
                            onChange={(e) =>
                                setManualPlace({
                                    ...manualPlace,
                                    name: e.target.value.slice(0, 60),
                                })
                            }
                        />
                        <TextField
                            label={t('chart.manualLat')}
                            value={manualPlace.lat}
                            onChange={(e) =>
                                setManualPlace({
                                    ...manualPlace,
                                    lat: e.target.value,
                                })
                            }
                            inputProps={{ inputMode: 'decimal' }}
                            error={error === 'lat'}
                            helperText={
                                error === 'lat' ? t('chart.error.lat') : ''
                            }
                        />
                        <TextField
                            label={t('chart.manualLon')}
                            value={manualPlace.lon}
                            onChange={(e) =>
                                setManualPlace({
                                    ...manualPlace,
                                    lon: e.target.value,
                                })
                            }
                            inputProps={{ inputMode: 'decimal' }}
                            error={error === 'lon'}
                            helperText={
                                error === 'lon' ? t('chart.error.lon') : ''
                            }
                        />
                        <Autocomplete
                            freeSolo
                            options={zones}
                            inputValue={manualPlace.zone}
                            onInputChange={(_, value) =>
                                setManualPlace({ ...manualPlace, zone: value })
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={t('chart.manualZone')}
                                    error={error === 'zone'}
                                    helperText={
                                        error === 'zone'
                                            ? t('chart.error.zone')
                                            : ''
                                    }
                                />
                            )}
                        />
                        {index && (
                            <Button
                                size="small"
                                onClick={() => setManual(false)}
                                sx={{ alignSelf: 'flex-start' }}
                            >
                                {t('chart.manualBack')}
                            </Button>
                        )}
                    </Stack>
                )}

                {error === 'calc' && (
                    <Alert severity="error">{t('chart.error.calc')}</Alert>
                )}
                {dismissed && (
                    <Alert severity="info">{t('chart.dismissed')}</Alert>
                )}
                <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={busy}
                >
                    {t('chart.submit')}
                </Button>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    align="center"
                >
                    {t('chart.unlockNote')}
                </Typography>
            </Stack>

            {saved.length > 0 && (
                <Box sx={{ mt: 4 }}>
                    <Typography
                        variant="overline"
                        color="primary"
                        sx={{ letterSpacing: '0.3em' }}
                    >
                        {t('chart.saved')}
                    </Typography>
                    <Stack spacing={1} sx={{ mt: 1 }}>
                        {saved.map((c) => (
                            <Paper
                                key={c.id}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    background: 'rgba(21,14,36,.85)',
                                }}
                            >
                                <Box
                                    component="button"
                                    type="button"
                                    onClick={() => {
                                        playCue('ui');
                                        navigate(`/birth-chart/${c.id}`);
                                    }}
                                    sx={{
                                        flex: 1,
                                        minWidth: 0,
                                        p: 1.5,
                                        border: 0,
                                        background: 'none',
                                        color: 'text.primary',
                                        font: 'inherit',
                                        textAlign: 'left',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <Typography
                                        sx={{ fontFamily: 'Cinzel, serif' }}
                                    >
                                        {c.name || t('chart.mine')}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {new Date(
                                            c.input.year,
                                            c.input.month - 1,
                                            c.input.day
                                        ).toLocaleDateString(language, {
                                            dateStyle: 'medium',
                                        })}
                                        {' · '}
                                        {c.input.place.name}
                                    </Typography>
                                </Box>
                                <IconButton
                                    aria-label={t('chart.delete')}
                                    onClick={() => setDeleting(c.id)}
                                >
                                    <DeleteOutlineRoundedIcon />
                                </IconButton>
                            </Paper>
                        ))}
                    </Stack>
                </Box>
            )}

            <Dialog open={deleting !== null} onClose={() => setDeleting(null)}>
                <DialogTitle>{t('chart.deleteTitle')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('chart.deleteBody')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleting(null)}>
                        {t('chart.cancel')}
                    </Button>
                    <Button
                        onClick={() => {
                            if (deleting) dispatch(deleteChart(deleting));
                            setDeleting(null);
                        }}
                    >
                        {t('chart.delete')}
                    </Button>
                </DialogActions>
            </Dialog>
        </PageShell>
    );
};

/** A saved chart, or the form when no chart is named. */
export const BirthChartPage: React.FC = () => {
    const { id } = useParams();
    const { t } = useI18n();
    const navigate = useNavigate();
    const saved = useSelector((s: RootState) =>
        s.tarot.charts.find((c) => c.id === id)
    );
    if (!id) return <ChartForm />;
    return (
        <PageShell to="/">
            {saved ? (
                <>
                    <ChartView key={saved.id} saved={saved} />
                    <Stack spacing={1} alignItems="center" sx={{ mt: 4 }}>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                playCue('ui');
                                navigate('/birth-chart');
                            }}
                        >
                            {t('chart.another')}
                        </Button>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            align="center"
                        >
                            {t('chart.anotherHint')}
                        </Typography>
                    </Stack>
                </>
            ) : (
                <Typography
                    color="text.secondary"
                    align="center"
                    sx={{ mt: 4 }}
                >
                    {t('chart.notFound')}
                </Typography>
            )}
        </PageShell>
    );
};
